import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../context/UnifiedAuthContext';
import { Trophy, Zap, Calendar, Sparkles, Menu, X, BarChart3, Image, MessageSquare, BookOpen, Shield, ChevronRight, Crown, CloudRain } from 'lucide-react';
import { haptics } from '../utils/haptics';

// Routes where the tab bar should be hidden
const HIDDEN_ROUTES = ['/live', '/live/chat', '/live/display', '/printScorecard'];
const HIDDEN_PREFIXES = ['/auth'];

const tabs = [
  {
    id: 'home',
    label: 'Home',
    icon: Trophy,
    route: '/',
    match: (path) => path === '/' || path === '/leaderboard',
  },
  {
    id: 'live',
    label: 'Live',
    icon: Zap,
    route: '/live/leaderboard',
    match: (path) => path.startsWith('/live'),
  },
  {
    id: 'events',
    label: 'Events',
    icon: Calendar,
    route: '/events',
    match: (path) => path === '/events',
  },
  {
    id: 'fantasy',
    label: 'Fantasy',
    icon: Sparkles,
    route: '/fantasy-golf',
    match: (path) => path.startsWith('/fantasy'),
  },
  {
    id: 'weather',
    label: 'Weather',
    icon: CloudRain,
    route: '/weather',
    match: (path) => path === '/weather',
  },
  {
    id: 'more',
    label: 'More',
    icon: Menu,
    route: null,
    match: () => false,
  },
];

const BottomTabBar = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  // Toggle body class for padding
  useEffect(() => {
    if (!isMobile) {
      document.body.classList.remove('has-bottom-tabs');
      return;
    }

    const shouldHide =
      HIDDEN_ROUTES.includes(location.pathname) ||
      HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

    if (shouldHide) {
      document.body.classList.remove('has-bottom-tabs');
    } else {
      document.body.classList.add('has-bottom-tabs');
    }

    return () => document.body.classList.remove('has-bottom-tabs');
  }, [isMobile, location.pathname]);

  // Close "More" panel on route change
  useEffect(() => {
    setIsMoreOpen(false);
  }, [location.pathname]);

  const handleTabPress = useCallback(
    (tab) => {
      haptics.impact('light');
      if (tab.id === 'more') {
        setIsMoreOpen((prev) => !prev);
        return;
      }
      setIsMoreOpen(false);
      navigate(tab.route);
    },
    [navigate]
  );

  if (!isMobile) return null;

  // Hide on certain routes
  const shouldHide =
    HIDDEN_ROUTES.includes(location.pathname) ||
    HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (shouldHide) return null;

  const isAdmin = hasRole('admin') || hasRole('moderator') || hasRole('superadmin') || hasRole('scorekeeper');

  return (
    <>
      {/* More Panel Overlay */}
      {isMoreOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setIsMoreOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setIsMoreOpen(false); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Panel */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-navy-900 rounded-t-2xl shadow-2xl pb-[calc(60px+env(safe-area-inset-bottom,0px))] bottom-sheet-enter"
            role="menu"
            aria-label="More options"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-navy-600" aria-hidden="true" />
            </div>

            <nav className="px-4 pb-4 space-y-1">
              <MoreLink icon={Image} label="Gallery" to="/gallery" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
              <MoreLink icon={Crown} label="Fantasy Leaderboard" to="/fantasy-leaderboard" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
              <MoreLink icon={BarChart3} label="Stats" to="/quotas" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
              <MoreLink icon={MessageSquare} label="Suggestions" to="/suggestions" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
              <MoreLink icon={BookOpen} label="Rules" to="/rules" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />

              {isAdmin && (
                <>
                  <div className="border-t border-gray-200 dark:border-navy-700 my-2" />
                  <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Admin
                  </div>
                  {/* Payouts is reached from Live Scoring, since it only works
                      on a game day with an active event. */}
                  <MoreLink icon={Shield} label="Record Results" to="/admin/record-results" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Manage Events" to="/admin/manage-event" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Manage Players" to="/admin/manage-players" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Live Scoring" to="/admin/live-scoring" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Pairings" to="/admin/pairings" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Playoffs" to="/admin/playoffs" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                  <MoreLink icon={Shield} label="Dashboard" to="/admin/dashboard" navigate={navigate} setIsMoreOpen={setIsMoreOpen} />
                </>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div
        className="bottom-tab-bar fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md dark:bg-navy-900/95 border-t border-gray-200 dark:border-navy-800"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-around h-14">
          {tabs.map((tab) => {
            const isActive = tab.id === 'more' ? isMoreOpen : tab.match(location.pathname);
            const Icon = tab.id === 'more' && isMoreOpen ? X : tab.icon;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabPress(tab)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 focus-visible:ring-inset ${
                  isActive
                    ? 'text-forest-600 dark:text-forest-400'
                    : 'text-gray-500 dark:text-gray-500'
                }`}
                aria-label={tab.label}
                {...(tab.id === 'more' ? { 'aria-expanded': isMoreOpen, 'aria-haspopup': 'true' } : {})}
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="text-[10px] mt-0.5 font-medium leading-tight">
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

const MoreLink = ({ icon: Icon, label, to, navigate, setIsMoreOpen }) => (
  <button
    onClick={() => {
      haptics.impact('light');
      navigate(to);
      setIsMoreOpen(false);
    }}
    className="flex items-center w-full px-3 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-500"
    role="menuitem"
  >
    <Icon className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-500" />
    <span className="flex-1 text-left font-medium">{label}</span>
    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
  </button>
);

export default BottomTabBar;
